import React, { useState } from 'react'
import { Grid, Box, Tooltip } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import FormLabel from '@material-ui/core/FormLabel'
import * as yup from 'yup'
import { Formik, Field } from 'formik'
import { useIntl, FormattedMessage } from 'react-intl'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import LiquidCheckboxAdapter from '@src/components/LiquidCheckboxAdapter'
import messages from '../../../../messages'
import LiquidSelectAdapter from '@src/components/LiquidSelectAdapter'
import ValidationErrors from '../../../../validations/ValidationErrors'
import { DefaultValue } from './FastSequenceInfo'
import { BitubeProperties } from '../../enum'
import InfoIcon from '@material-ui/icons/Info'
import { biTubeConfigContext } from '../../BiTube'
import { useFastduplexRuleValidationMutation } from '@src/mutations/ConfiguratorTubesMutation.generated'
import {
  getThreeModValue,
  getFiveModValue,
} from '@src/routes/Configurators/components/OptionalModifications/ModificationResponse'
import OligoNameWarning from '../../../../validations/oligoNameWarning'
import {
  validateTextUpdated as validateText,
  convertToASCII,
  checkValidKey,
} from '@src/routes/Configurators/utils/util'
import { ListError } from '../../Reducer'
import { ConfiguratorValues } from '../../../../utils/constants'
import styles from '@src/routes/Configurators/utils/styles'
import clsx from 'clsx'

const nameKey = messages.CONF_OLIGO_NAME_KEY.defaultMessage

const useStyles = makeStyles((theme: Theme) => {
  return {
    baseCount: {
      fontSize: theme.typography.pxToRem(14),
      margin: theme.spacing(0, 0, 2),
    },
    baseContent: {
      fontSize: theme.typography.pxToRem(12),
    },
    InfoIcon: {
      position: 'relative',
      fontSize: theme.typography.pxToRem(16),
      top: theme.spacing(0.5),
      marginLeft: theme.spacing(1.5),
    },
    arrow: {
      color: theme.palette.common.white,
    },
    InfoPopup: {
      maxWidth: theme.spacing(65),
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      boxShadow: theme.shadows[4],
      padding: theme.spacing(5),
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.common.black,
      borderRadius: '2%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    sequenceInput: {
      '&.Mui-error': {
        border: '0 none',
        borderBottom: `1px solid ${theme.palette.error.main}`,
      },
      '&.Mui-error:hover': {
        border: '0 none',
        borderBottom: `1px solid ${theme.palette.error.main}`,
      },
      '&.Mui-error.Mui-focused': {
        border: '0 none',
        borderBottom: `1px solid ${theme.palette.error.main}`,
      },
      '& input': {
        '&:-webkit-autofill': {
          transition: 'background-color 5000s ease-in-out 0s',
        },
      },
    },
    sequenceLabel: {
      width: theme.spacing(120),
      '& label': {
        fontSize: theme.typography.pxToRem(14),
      },
    },
    label: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.palette.text.primary,
      margin: theme.spacing(0, 0, 1),
      display: 'inline-flex',
    },
    checkboxLabel: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.text.primary,
      margin: theme.spacing(0, 0, 1),
      display: 'inline-flex',
    },
    mt20: {
      marginTop: theme.spacing(5),
    },
    base: {
      paddingLeft: theme.spacing(38),
      paddingTop: theme.spacing(2),
    },
    mt30: {
      marginTop: theme.spacing(10),
    },
    autoGenerate: {
      paddingLeft: theme.spacing(4),
    },
    alignTop2: {
      marginTop: theme.spacing(1.7),
    },
    checked: {},
    validateError: {
      marginLeft: theme.spacing(-4),
      marginTop: theme.spacing(-4),
      '&.Mui-error.Mui-focused': {
        border: '0 none',
        borderBottom: `1px solid ${theme.palette.error.main}`,
      },
      paddingLeft: theme.spacing(4),
      paddingTop: theme.spacing(2),
    },
    warningBox: {
      padding: theme.spacing(3, 0, 0, 0),
    },
    overhangpadding: {
      paddingTop: theme.spacing(2),
    },
  }
})

const MirnaSenceStrand = (props) => {
  const { modificationData, productDetails } = props
  const classes = useStyles()
  const GlobalCSS = styles()
  const [nameValidation, setNameValidation] = useState<string[]>([])
  const [fiveModValidation, setFiveModValidation] = useState<string[]>([])
  const intl = useIntl()
  const oligoNameMaxLength = Number(
    intl.formatMessage(messages.CONF_CHINA_OLIGO_NAME_MAX_LENGTH)
  )
  const [fastduplexRuleValidation] = useFastduplexRuleValidationMutation()
  let splChar = ''
  const { bitubeState, biTubeDispatch } = React.useContext(biTubeConfigContext)
  const { sessionData, currentOligoIndex, validateOnClick } = bitubeState
  const [senseName, setSenseName] = useState<string | null | undefined>('')
  const [senseNameCheck, setSenseNameCheck] = useState<
    boolean | null | undefined
  >(false)
  const [nameChanged, setNameChanged] = useState<boolean | null | undefined>(
    false
  )
  const [nameDisabled, setNameDisabled] = useState(false)
  const [threeMod, setThreeMod] = useState<string | null | undefined>('')
  const [fiveMod, setFiveMod] = useState<string | null | undefined>('')
  const [fiveModSelected, setFiveModSelected] = useState(false)
  const [sessionDataChanged, setSessionDataChanged] = useState<
    boolean | null | undefined
  >(false)
  const antisenseNameVal = sessionData[currentOligoIndex].antisenceName
  let initialLoad = false

  let overhangValue = ''

  const getOverhang = () => {
    let overhang = ''
    modificationData.modifications.forEach((modifications) => {
      if (
        modifications.modificationType === DefaultValue.OverHang &&
        modifications.category.length === 1 &&
        modifications.category[0].modifications.length === 1
      ) {
        overhang = intl.formatMessage(
          checkValidKey(
            messages[modifications.category[0].modifications[0].displayName],
            modifications.category[0].modifications[0].displayName
          )
        )
        overhangValue = modifications.category[0].modifications[0].value
      }
    })
    return overhang
  }
  const ruleValidation = async (obj) => {
    const response = await fastduplexRuleValidation({
      variables: { input: obj, productInput: productDetails },
    })
    const { data } = response
    if (data && !data.fastduplexRuleValidation.serverError) {
      if (data.fastduplexRuleValidation.listErrors) {
        const reviewError: any[] = []
        let hasFiveMod = false
        let hasSameErr = false
        const fiveModeError: any = []
        data.fastduplexRuleValidation.listErrors.forEach((item) => {
          delete item?.__typename
          if (item?.key === BitubeProperties.MirnaFiveMod) {
            hasFiveMod = true
            fiveModeError.push(item)
            if (fiveModSelected) {
              sessionData[currentOligoIndex].listError.forEach((err) => {
                //if the same error is there in sessiondata, do not push the error
                if (err.key === BitubeProperties.MirnaFiveMod) {
                  hasSameErr = true
                }
              })
              if (!hasSameErr) {
                sessionData[currentOligoIndex].listError.push({
                  key: fiveModeError[0]?.key,
                  value: fiveModeError[0]?.value,
                })
              }
            }
          }
          if (item?.key === BitubeProperties.ReviewError) {
            reviewError.push(item)
          }
        })

        if (!hasFiveMod) {
          const fiveModError: ListError[] = []
          sessionData[currentOligoIndex].listError.forEach((err) => {
            if (err.key === BitubeProperties.MirnaFiveMod) {
              fiveModError.push(err)
            }
          })
          const array = sessionData[currentOligoIndex].listError
          fiveModError.forEach((e) => {
            const index = array.indexOf(e)
            if (index !== -1) {
              array.splice(index, 1)
            }
          })
        }
        // Updating the ReviewError after RuleValidation
        const updateReviewError: ListError[] = []
        sessionData[currentOligoIndex].listError.forEach((err) => {
          if (err.key === BitubeProperties.ReviewError) {
            updateReviewError.push(err)
          }
        })
        const array = sessionData[currentOligoIndex].listError
        updateReviewError.forEach((e) => {
          const index = array.indexOf(e)
          if (index !== -1) {
            array.splice(index, 1)
          }
        })
        if (reviewError.length !== 0) {
          sessionData[currentOligoIndex].listError.push({
            key: reviewError[0].key,
            value: reviewError[0].value,
          })
        }
      }

      biTubeDispatch({
        type: 'UPDATE_ERROR_RESPONSE',
        data: data.fastduplexRuleValidation,
      })
      biTubeDispatch({
        type: 'UPDATE_INPUT',
        data: [...bitubeState.sessionData],
      })
    } else {
      biTubeDispatch({
        type: 'SET_PAGE_ERROR',
        data: ConfiguratorValues.InternalServerError,
      })
      window.scrollTo(0, 0)
    }
  }

  const ruleValidationONNameChange = async (obj) => {
    const response = await fastduplexRuleValidation({
      variables: { input: obj, productInput: productDetails },
    })
    const { data } = response
    if (data && !data.fastduplexRuleValidation.serverError) {
      if (data.fastduplexRuleValidation.listErrors) {
        const errorList = data.fastduplexRuleValidation.listErrors
        const error: any[] = []
        errorList.forEach((item) => {
          delete item?.__typename
          if (item?.key === BitubeProperties.ReviewError) {
            error.push(item)
          }
        })
        // Updating the reviewError after the rulevalidation
        const reviewError: ListError[] = []
        sessionData[currentOligoIndex].listError.forEach((err) => {
          if (err.key === BitubeProperties.ReviewError) {
            reviewError.push(err)
          }
        })
        const array = sessionData[currentOligoIndex].listError
        reviewError.forEach((e) => {
          const index = array.indexOf(e)
          if (index !== -1) {
            array.splice(index, 1)
          }
        })
        if (error.length !== 0) {
          sessionData[currentOligoIndex].listError.push({
            key: error[0].key,
            value: error[0].value,
          })
        }
        biTubeDispatch({
          type: 'UPDATE_INPUT',
          data: [...bitubeState.sessionData],
        })
      }
    } else {
      biTubeDispatch({
        type: 'SET_PAGE_ERROR',
        data: ConfiguratorValues.InternalServerError,
      })
      window.scrollTo(0, 0)
    }
  }

  const checkValidation = (response) => {
    setFiveModSelected(false)
    setFiveModValidation([])
    const fivemodError: string[] = []

    if (bitubeState.seqLength !== 0 && response.listErrors) {
      response.listErrors.forEach((obj) => {
        if (obj.key === BitubeProperties.MirnaFiveMod) {
          fivemodError.push(obj.value)
        }
      })
      setFiveModValidation(fivemodError)
    }
  }

  const checkOverrideValidation = (response) => {
    setNameValidation([])
    setFiveModValidation([])
    const nameError: string[] = []
    const modeError: string[] = []
    if (response.listErrors) {
      response.listErrors.forEach((obj) => {
        if (obj.key === nameKey) {
          nameError.push(obj.value)
        }
        if (obj.key === BitubeProperties.MirnaFiveMod) {
          modeError.push(obj.value)
        }
      })
    }
    setFiveModValidation(modeError)
    setNameValidation(nameError)
  }

  const handleGenSenceName = () => {
    biTubeDispatch({
      type: 'IS_PAGE_RELOAD',
      data: false,
    })
    sessionData[currentOligoIndex].senseNameCheck = !senseNameCheck
    biTubeDispatch({
      type: 'UPDATE_INPUT',
      data: [...bitubeState.sessionData],
    })
    biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: false })
    biTubeDispatch({ type: 'SET_STEP_NO', data: 0 })
    setSenseNameCheck(!senseNameCheck)
    setSessionDataChanged(true)
  }
  const handleOnBlur = (event) => {
    const { name, value } = event.target
    if (name === BitubeProperties.NameKey) {
      sessionData[currentOligoIndex][name] = value.trim()
      setNameChanged(true)
    } else {
      sessionData[currentOligoIndex][name] = value
    }
    biTubeDispatch({
      type: 'UPDATE_INPUT',
      data: [...bitubeState.sessionData],
    })
    if (name === BitubeProperties.NameKey) {
      biTubeDispatch({
        type: 'UPDATE_ERROR_DATA',
        data: { ...bitubeState.errorData, [name]: value.trim() },
      })
    } else {
      biTubeDispatch({
        type: 'UPDATE_ERROR_DATA',
        data: { ...bitubeState.errorData, [name]: value },
      })
    }
  }

  //function to extract the special characters from the string passed
  const getOligoNameSpecialCharacter = (val): string => {
    setSenseName(val)
    splChar = ''
    val &&
      val.split('').forEach((c) => {
        if (
          !c.match(/^[a-zA-Z0-9_#*().,:+.[\-\]/ ]*$/) &&
          !splChar.includes(c)
        ) {
          if (splChar === '') {
            splChar = c
          } else {
            splChar = splChar + ', ' + c
          }
        }
      })
    return splChar
  }

  const handleName = (val) => {
    const oligoNameSpecialCharacter = getOligoNameSpecialCharacter(val)
    biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: false })
    biTubeDispatch({ type: 'SET_STEP_NO', data: 0 })
    const validator = yup
      .string()
      .trim()
      .required(intl.formatMessage(messages.CONF_REQUIRED_FIELD))
      .max(
        oligoNameMaxLength,
        intl.formatMessage(messages.CONF_OLIGO_NAME_MAX_LENGTH_MASSAGE) +
          oligoNameMaxLength
      )
      .matches(
        /^[a-zA-Z0-9_#*().,:+.[\-\]/ ]*$/,
        intl.formatMessage(messages.CONF_OLIGO_NAME_SPECIAL_CHARACTER_MASSAGE) +
          oligoNameSpecialCharacter
      )
    const errors: any[] = []
    try {
      setNameValidation([])
      validator.validateSync(val, { abortEarly: false })
      let nameError: ListError = {}
      //if there is only one error and thats name error then clear the list error
      sessionData[currentOligoIndex].listError.forEach((err) => {
        if (err.key === nameKey) {
          nameError = err
        }
      })
      const array = sessionData[currentOligoIndex].listError
      const index = array.indexOf(nameError)
      if (index !== -1) {
        array.splice(index, 1)
      }
    } catch (error: any) {
      for (const err of error.errors) {
        let hasSameErr = false
        sessionData[currentOligoIndex].listError.forEach((error) => {
          if (error.key === BitubeProperties.NameKey && error.value === err) {
            hasSameErr = true
          }
        })
        errors.push(err)
        if (!hasSameErr) {
          sessionData[currentOligoIndex].listError.push({
            key: nameKey,
            value: err,
          })
        }
        if (err === intl.formatMessage(messages.CONF_REQUIRED_FIELD)) {
          // if pushing required error for name in sessionData then remove remaining errors there for name
          const nameError: ListError[] = []
          sessionData[currentOligoIndex].listError.forEach((error) => {
            if (
              error.key === BitubeProperties.NameKey &&
              (error.value !==
                intl.formatMessage(messages.CONF_REQUIRED_FIELD) ||
                error.value !== messages.CONF_REQUIRED_FIELD.defaultMessage)
            ) {
              nameError.push(error)
            }
          })
          const array = sessionData[currentOligoIndex].listError
          nameError.forEach((e) => {
            const index = array.indexOf(e)
            if (index !== -1) {
              array.splice(index, 1)
            }
          })
        } else {
          // if pushing errors other than required error remove Required error for sence Name from session
          const nameErrorReq: ListError[] = []
          sessionData[currentOligoIndex].listError.forEach((error) => {
            if (
              error.key === BitubeProperties.NameKey &&
              (error.value ===
                intl.formatMessage(messages.CONF_REQUIRED_FIELD) ||
                error.value === messages.CONF_REQUIRED_FIELD.defaultMessage)
            ) {
              nameErrorReq.push(error)
            }
          })
          const array = sessionData[currentOligoIndex].listError
          nameErrorReq.forEach((e) => {
            const index = array.indexOf(e)
            if (index !== -1) {
              array.splice(index, 1)
            }
          })
        }
      }
      setNameValidation(errors)
    }
    return null
  }
  const checkTotalValidation = (listErrors) => {
    const nameError: string[] = []
    const modError: string[] = []
    if (listErrors) {
      listErrors.forEach((obj) => {
        if (obj.key === nameKey) {
          nameError.push(obj.value)
        }
        if (obj.key === BitubeProperties.MirnaFiveMod) {
          modError.push(obj.value)
        }
      })
    }
    setFiveModValidation(modError)
    setNameValidation(nameError)
  }
  const handleDropDownSelect = (event) => {
    const { name, value } = event.target
    if (name === BitubeProperties.ThreeMod) {
      setThreeMod(value)
    }
    if (name === BitubeProperties.FiveMod) {
      setFiveModSelected(true)
      setFiveMod(value)
      setFiveModValidation([])
    }

    sessionData[currentOligoIndex][name] = value
    biTubeDispatch({
      type: 'UPDATE_INPUT',
      data: [...bitubeState.sessionData],
    })
    biTubeDispatch({
      type: 'UPDATE_ERROR_DATA',
      data: {
        ...bitubeState.errorData,
        [name]: value,
      },
    })
    biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: false })
    biTubeDispatch({ type: 'SET_STEP_NO', data: 0 })
    setSessionDataChanged(true)
  }

  React.useEffect(() => {
    if (
      bitubeState.overriddenFlag &&
      bitubeState.overiddenNumber !== 0 &&
      bitubeState.sessionData[bitubeState.currentOligoIndex].isOverridden
    ) {
      const listErrors = bitubeState.errorResponseJson.listErrors
      checkTotalValidation(listErrors)
    }
    biTubeDispatch({ type: 'HAS_OVERRIDDEN_FLAG', data: false })
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bitubeState.overriddenFlag])
  React.useEffect(() => {
    if (
      !nameValidation.length &&
      !sessionData[currentOligoIndex].listError.length
    ) {
      biTubeDispatch({
        type: 'SET_PAGE_ERROR',
        data: '',
      })
    }
  }, [nameValidation])

  React.useEffect(() => {
    if (validateOnClick) {
      const isReviewErrorsResolved =
        sessionData[currentOligoIndex].listError.length === 1 &&
        sessionData[currentOligoIndex].listError[0].key ===
          BitubeProperties.ReviewError
      if (isReviewErrorsResolved) {
        biTubeDispatch({
          type: 'SET_PAGE_ERROR',
          data: '',
        })
        biTubeDispatch({
          type: 'VALIDATE_ON_CLICK',
          data: false,
        })
      }
    }
  }, [nameValidation])

  React.useEffect(() => {
    if (
      productDetails.isCSRSupervisor &&
      bitubeState.overiddenNumber === 0 &&
      bitubeState.deleteLastOligo === 1
    ) {
      checkTotalValidation(sessionData[currentOligoIndex].listError)
    }
    if (!initialLoad && sessionData[currentOligoIndex].autoGenerateOligo) {
      biTubeDispatch({
        type: 'IS_PAGE_RELOAD',
        data: false,
      })
    } else {
      setSenseName(sessionData[currentOligoIndex].name)
      initialLoad = false
    }
    biTubeDispatch({
      type: 'UPDATE_SEQUENCE_LENGTH',
      data: sessionData[currentOligoIndex].sequence?.length,
    })
    let threeModVal = sessionData[currentOligoIndex].threeMod
    if (
      threeModVal === '' ||
      threeModVal === null ||
      threeModVal === undefined
    ) {
      threeModVal = BitubeProperties.None
      sessionData[currentOligoIndex].threeMod = BitubeProperties.None
    }
    let fiveModVal = sessionData[currentOligoIndex].fiveMod
    if (fiveModVal === '' || fiveModVal === null || fiveModVal === undefined) {
      fiveModVal = BitubeProperties.None
      sessionData[currentOligoIndex].fiveMod = BitubeProperties.None
    }
    sessionData[currentOligoIndex].customerOverhang = overhangValue
    setFiveMod(fiveModVal)
    setThreeMod(threeModVal)
    setSenseNameCheck(sessionData[currentOligoIndex].senseNameCheck)
    if (sessionData[currentOligoIndex].senseNameCheck) {
      setNameDisabled(true)
    } else {
      setNameDisabled(false)
    }
    biTubeDispatch({
      type: 'UPDATE_ERROR_DATA',
      data: {
        ...bitubeState.errorData,
        threeMod: threeModVal,
        fiveMod: fiveModVal,
        customerOverhang: overhangValue,
      },
    })
    // TODO: need a test before we refactor this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData, bitubeState.currentOligoIndex])

  React.useEffect(() => {
    setSenseName(sessionData[currentOligoIndex].name)
    checkTotalValidation(sessionData[currentOligoIndex].listError)
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bitubeState.currentOligoIndex])

  React.useEffect(() => {
    if (bitubeState.clearSessionData) {
      setSenseName('')
    }
    // TODO: need a test before we refactor this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bitubeState.clearSessionData])

  React.useEffect(() => {
    if (sessionData[currentOligoIndex].senseNameCheck) {
      if (sessionData[currentOligoIndex].antisenceName !== '') {
        setSenseName(sessionData[currentOligoIndex].antisenceName + '_s')
        sessionData[currentOligoIndex].name =
          sessionData[currentOligoIndex].antisenceName + '_s'

        bitubeState.errorData.name =
          sessionData[currentOligoIndex].antisenceName + '_s'
      } else {
        setSenseName('_s')
        sessionData[currentOligoIndex].name = '_s'
        bitubeState.errorData.name = '_s'
      }
      biTubeDispatch({
        type: 'UPDATE_INPUT',
        data: [...bitubeState.sessionData],
      })
      biTubeDispatch({
        type: 'UPDATE_ERROR_DATA',
        data: {
          ...bitubeState.errorData,
          name: sessionData[currentOligoIndex].name,
        },
      })
      setNameValidation([])
      setNameDisabled(true)

      let nameError: ListError = {}
      sessionData[currentOligoIndex].listError.forEach((err) => {
        if (err.key === nameKey) {
          nameError = err
        }
      })
      const array = sessionData[currentOligoIndex].listError
      const index = array.indexOf(nameError)
      if (index !== -1) {
        array.splice(index, 1)
      }
    } else {
      //if the auto gen name is unchecked, perform front end validation
      const spcChar = getOligoNameSpecialCharacter(
        sessionData[currentOligoIndex].name
      )
      const errors: any[] = []
      const validator = yup
        .string()
        .max(
          oligoNameMaxLength,
          intl.formatMessage(messages.CONF_OLIGO_NAME_MAX_LENGTH_MASSAGE) +
            oligoNameMaxLength
        )
        .matches(
          /^[a-zA-Z0-9_#*().,:+.[\-\]/ ]*$/,
          intl.formatMessage(
            messages.CONF_OLIGO_NAME_SPECIAL_CHARACTER_MASSAGE
          ) + spcChar
        )
      try {
        validator.validateSync(senseName, { abortEarly: false })
      } catch (error: any) {
        for (const err of error.errors) {
          errors.push(err)
        }
        setNameValidation(errors)
      }
      setNameDisabled(false)
    }
    // TODO: need a test before we refactor this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [antisenseNameVal, sessionData[currentOligoIndex].senseNameCheck])

  React.useEffect(() => {
    if (bitubeState.seqLength !== 0 && sessionDataChanged) {
      ruleValidation(bitubeState.errorData)
      setSessionDataChanged(false)
    }
    if (nameChanged) {
      ruleValidationONNameChange(bitubeState.errorData)
      setNameChanged(false)
    }
    // TODO: need a test before we refactor this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bitubeState.errorData])

  React.useEffect(() => {
    if (
      sessionData[currentOligoIndex].baseCount !== 0 ||
      sessionData[currentOligoIndex].name?.length !== 0 ||
      bitubeState.sessionData[bitubeState.currentOligoIndex].isOverridden !==
        null
    ) {
      if (
        productDetails.isCSRSupervisor &&
        !bitubeState.sessionData[bitubeState.currentOligoIndex].isOverridden &&
        bitubeState.overiddenNumber !== 0
      ) {
        checkOverrideValidation(bitubeState.errorResponseJson)
      } else if (fiveModSelected) {
        checkValidation(bitubeState.errorResponseJson)
      }
      if (sessionData[currentOligoIndex].isOverridden) {
        setFiveModValidation([])
      }
      const error: any[] = []
      if (bitubeState.errorResponseJson.listErrors) {
        bitubeState.errorResponseJson.listErrors.forEach((item) => {
          delete item.__typename
        })
        if (error.length > 0) {
          biTubeDispatch({ type: 'IS_CONFIRM_ORDER', data: false })
          biTubeDispatch({ type: 'SET_STEP_NO', data: 0 })
        }
      }
    }
    // TODO: need a test before we refactor this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bitubeState.errorResponseJson])

  React.useEffect(() => {
    if (
      !bitubeState.isValidTotalValidation &&
      bitubeState.totalValidation[bitubeState.currentOligoIndex]
    ) {
      checkTotalValidation(
        bitubeState.totalValidation[bitubeState.currentOligoIndex].listErrors
      )
    } else {
      setNameValidation([])
      setFiveModValidation([])
    }
    // TODO: need a test before we refactor this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bitubeState.totalValidation])

  React.useEffect(() => {
    if (bitubeState.errorOnPageRefresh) {
      checkTotalValidation(sessionData[currentOligoIndex].listError)
    }
  }, [bitubeState.errorOnPageRefresh])

  React.useEffect(() => {
    if (bitubeState.deleteLastOligo) {
      setNameValidation([])
      setFiveModValidation([])
    }
  }, [
    bitubeState.oligoCount,
    bitubeState.deleteLastOligo,
    bitubeState.addNewDuplex,
  ])
  React.useEffect(() => {
    if (bitubeState.deleteSelected === 2) {
      checkTotalValidation(sessionData[currentOligoIndex].listError)
      biTubeDispatch({
        type: 'IS_DELETE_SELECTED',
        data: 0,
      })
    }
  }, [bitubeState.deleteSelected])

  const showSenceSequence = () => {
    if (sessionData[currentOligoIndex].isOverridden) {
      return false
    }
    return (
      bitubeState.antisenseSeqLength === 0 ||
      bitubeState.antisenseSeqLength === undefined ||
      !bitubeState.isAntiSeqValid
    )
  }

  const mirnaSenceComponent = () => {
    return (
      <Grid container spacing={3} direction="row" className={classes.mt20}>
        <Grid item sm={3}>
          <FormLabel className={classes.label}>
            <FormattedMessage {...messages.CONF_5_MOD_standard} />
          </FormLabel>
          <Field
            name="fiveMod"
            component={LiquidSelectAdapter}
            label=""
            value={fiveMod}
            options={[
              ...getFiveModValue(
                DefaultValue.FiveModiFication,
                DefaultValue.Category,
                modificationData,
                bitubeState.errorResponseJson
              ),
            ]}
            onChange={handleDropDownSelect}
          />
          <div
            className={clsx(classes.baseContent, GlobalCSS.globalHelpTextColor)}
          >
            <FormattedMessage
              {...messages.CONF_MIRNA_FIVE_MODIFICATION_HELP_TEXT}
            />
          </div>
          <ValidationErrors errors={fiveModValidation} />
        </Grid>
        <Grid item xs={3}>
          <FormLabel className={classes.label}>
            <FormattedMessage {...messages.CONF_SENSE_SEQUENCE} />
            &nbsp;
            <FormattedMessage {...messages.CONF_SEQUENCE_TYPE} />
          </FormLabel>
          <Grid className={clsx(GlobalCSS.breakword, classes.overhangpadding)}>
            {showSenceSequence()
              ? '--'
              : sessionData[currentOligoIndex].sequence?.length !== 0
                ? sessionData[currentOligoIndex].sequence
                : '--'}
          </Grid>
        </Grid>
        <Grid item sm={2}>
          <FormLabel className={classes.label}>
            <FormattedMessage {...messages.CONF_OVERHANG_LABEL} />
          </FormLabel>
          <Grid className={classes.overhangpadding}>{getOverhang()}</Grid>
        </Grid>
        <Grid item sm={3}>
          <FormLabel className={classes.label}>
            <FormattedMessage {...messages.CONF_3_MOD_standard} />
          </FormLabel>
          <Field
            name="threeMod"
            component={LiquidSelectAdapter}
            label=""
            value={threeMod}
            options={[
              ...getThreeModValue(
                DefaultValue.ThreeModification,
                DefaultValue.Category,
                modificationData,
                bitubeState.errorResponseJson
              ),
            ]}
            onChange={handleDropDownSelect}
          />
        </Grid>
      </Grid>
    )
  }
  return (
    <div className={classes.mt30}>
      <h4>
        <FormattedMessage {...messages.CONF_DUPLEX_SENSE_STRAD} />
        <Tooltip
          title={<FormattedMessage {...messages.CONF_DNA_OLIGO_NAME_TOOLTIP} />}
          classes={{ tooltip: classes.InfoPopup, arrow: classes.arrow }}
          arrow
          placement="bottom-end"
        >
          <InfoIcon
            aria-label="info"
            className={classes.InfoIcon}
            color="primary"
          ></InfoIcon>
        </Tooltip>
        <div>
          <Field
            name="senseNameCheck"
            component={LiquidCheckboxAdapter}
            color="primary"
            label={
              <FormLabel className={classes.checkboxLabel}>
                <FormattedMessage {...messages.CONF_MIRNA_USE_ANTISENCE_NAME} />
              </FormLabel>
            }
            value={senseNameCheck}
            onChange={handleGenSenceName}
            checked={senseNameCheck}
          />
        </div>
      </h4>
      <Box>
        <Formik
          initialValues={{}}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              actions.setSubmitting(false)
            }, 1000)
          }}
        >
          {() => (
            <>
              <Grid container spacing={10}>
                <Grid item xs={8} className={classes.alignTop2}>
                  <span className={classes.label}>
                    <FormattedMessage {...messages.CONF_SENSE_NAME} />
                  </span>
                  <Field
                    name="name"
                    component={LiquidInputAdapter}
                    className={classes.sequenceInput}
                    rootClass={classes.sequenceLabel}
                    spellcheck={'false'}
                    onBlur={handleOnBlur}
                    filled
                    value={senseName}
                    disabled={nameDisabled}
                    onChange={(e) => {
                      handleName(validateText(convertToASCII(e.target.value)))
                    }}
                  />
                  <div
                    className={clsx(
                      classes.baseContent,
                      GlobalCSS.globalHelpTextColor
                    )}
                  >
                    <FormattedMessage
                      {...messages.CONF_ASCII_CONF_OLIGO_NAME_SPECIAL_CHARACTER_MASSAGE}
                    />
                  </div>
                  <div className={classes.validateError}>
                    <ValidationErrors errors={nameValidation} />
                  </div>
                </Grid>
              </Grid>
              {senseName !== null && senseName !== undefined ? (
                senseName.trim().length > 18 ? (
                  <div className={classes.warningBox}>
                    <OligoNameWarning
                      warning={intl.formatMessage(
                        messages.CONF_OLIGO_NAME_WARNING
                      )}
                    />
                  </div>
                ) : (
                  ''
                )
              ) : (
                ''
              )}
              <div>{mirnaSenceComponent()}</div>
            </>
          )}
        </Formik>
      </Box>
    </div>
  )
}

export default MirnaSenceStrand
